import type { location } from '@cwos/api-client';

/**
 * Every word the Location & geofencing hub says, in one place (owner directive
 * §3/§4/§13: plain-language product copy, no raw enum names in primary copy —
 * the mode key appears only inside each card's advanced-details disclosure).
 *
 * Each string here states something the BACKEND actually does — the resolver
 * in apps/api/src/domains/location/policy.ts, the clock path in service.ts,
 * the purge sweep in retention.ts. Nothing below promises behaviour that does
 * not exist; when the behaviour changes, this file is the one that has to lie
 * or be updated.
 */

/**
 * The precedence sentence, exactly as the design doc states it
 * (docs/LOCATION-GEOFENCING-DESIGN.md; resolver: program → org → frozen
 * product default, PRODUCT_DEFAULT_POLICY).
 */
export const PRECEDENCE_SENTENCE =
  'program policy → organization policy → product default; the default is not stored so it cannot drift.';

/** When clock-event collection happens (directive §4 — the exact phrase). */
export const CLOCK_COLLECTION_WHEN = 'Only when the worker clocks in or out';

/** When breadcrumb collection happens (directive §4 — the exact phrase). */
export const BREADCRUMB_COLLECTION_WHEN = 'While a shift is running; stops at clock-out';

/**
 * What the worker's own policy card shows about shift collection. This QUOTES
 * the mobile PolicyCard's fixed bullet (packages/ui-mobile/src/components/
 * PolicyCard.tsx — "While clocked in / a location point every N minutes.
 * Never when you're clocked out.") rather than re-implementing its engine.
 */
export function workerBreadcrumbCardQuote(intervalSeconds: number): string {
  const minutes = Math.round(intervalSeconds / 60);
  return `While clocked in: a location point every ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}. Never when you’re clocked out.`;
}

/**
 * WHEN A POLICY CHANGE REACHES A WORKER — the sentence that was a promise.
 *
 * This read "A stricter mode is published for acknowledgement — workers are
 * told before it applies." Every clause of that was false and the product had
 * no mechanism for any of it:
 *
 *   - PUBLISHED: there is no publish step. `PUT /location/policy` writes the
 *     organization row and the resolver reads it on the next request.
 *   - FOR ACKNOWLEDGEMENT: there is no location-policy acknowledgement record
 *     anywhere in the schema. `PolicyCard` carries `ackVersion`/`ackDate` props
 *     that no caller can fill, because nothing produces them.
 *   - BEFORE IT APPLIES: `location_policy.effective_from` existed as a column
 *     and was written by the repository, but `resolveEffectivePolicy` never
 *     read it, `policyRowToDomain` never mapped it, and no response carried it.
 *     It deferred nothing. The admin console did not send it either.
 *
 * WHAT CHANGED ON THE SERVER (lane SCHEDULE, migration 1974000000000).
 * `scheduled_effective_at` replaced the date-only column, the resolver reads the
 * version in force rather than the live row, and a change scheduled for later
 * genuinely waits.
 *
 * WHAT CHANGED ON THIS SCREEN (lane SCHEDUI). The form can now send one, so
 * THERE ARE TWO SENTENCES AND NOT ONE. The constant below is the UNSCHEDULED
 * branch and it is still exactly true of an unscheduled save: those govern from
 * the moment they are written. A save the operator scheduled for later is a
 * different act with a different consequence and gets
 * {@link stricterModeNote}'s other branch. Do not fold them back into one
 * string: "as soon as you save" is true of one and false of the other, and a
 * sentence that is true for one branch and false for the other is the defect
 * this whole file was rewritten to remove.
 *
 * The acknowledgement clause is unchanged and still honest — there is no
 * acknowledgement record and no notification. What is gone is "and no effective
 * date", which was true when it was written and is now false: the operator is
 * choosing one directly above this note.
 */
/**
 * WHOSE WORKERS A SENTENCE ON THIS SCREEN IS ABOUT.
 *
 * Lane SCHEDUI wrote every consequence sentence for the ORGANIZATION row,
 * because that was the only scope with a saving form: "this applies to everyone
 * in your organization", "for everyone", "in force now for everyone". Each of
 * those is FALSE on a program override — a program policy reaches the workers on
 * a shift in that program and nobody else — and the founder's ruling of
 * 2026-09-08 gives program overrides the same saving surface. A sentence that is
 * true at one scope and false at the other is the defect this file's own header
 * says it exists to remove, so the audience is now a PARAMETER and never a
 * default.
 *
 * NO DEFAULT ARGUMENT ANYWHERE. A screen that forgot to say whose workers it is
 * about would print "your organization" over a program form — an over-claim
 * about how many people a tracking change reaches, made to the person deciding
 * whether to make it. The compiler asks instead.
 */
export type PolicyAudience = { kind: 'organization' } | { kind: 'program'; name: string };

export const ORGANIZATION_AUDIENCE: PolicyAudience = { kind: 'organization' };

export function programAudience(name: string): PolicyAudience {
  return { kind: 'program', name };
}

/** "everyone in your organization" · "everyone working in Peer Support". */
export function audienceEveryone(audience: PolicyAudience): string {
  return audience.kind === 'organization'
    ? 'everyone in your organization'
    : `everyone working in ${audience.name}`;
}

/** The short form, for a sentence that has already named the scope once. */
function audienceEveryoneShort(audience: PolicyAudience): string {
  return audience.kind === 'organization' ? 'everyone' : `everyone working in ${audience.name}`;
}

/**
 * THE ORGANIZATION-SCOPED STRING IS DERIVED FROM THE TEMPLATE, not kept beside
 * it. Two copies of one sentence is two places for the program page and the
 * organization page to start telling different stories about the same
 * mechanism; the constant below is exactly `stricterModeNoteFor(ORGANIZATION)`
 * and the suites that pin it word-for-word therefore pin the template too.
 */
export function stricterModeNoteFor(audience: PolicyAudience): string {
  return (
    `This applies to ${audienceEveryone(audience)} as soon as you save, and it changes what ` +
    'the app asks a worker at the door. Anyone already clocked in keeps the rules their shift ' +
    'started under until their next punch. Workers see the mode on their policy card in the ' +
    'app — there is no acknowledgement step and nothing announces the change, so tell them ' +
    'yourself if they need warning.'
  );
}

export const STRICTER_MODE_NOTE = stricterModeNoteFor(ORGANIZATION_AUDIENCE);

/**
 * THE TWO FLOORS, IN ONE SENTENCE, for a change the operator has scheduled.
 *
 * The founder's ruling: an increase or mixed change governs at "the LATER OF
 * its scheduled effective timestamp and the worker's next eligible disclosed
 * shift boundary". A screen that stated only the date would be telling half the
 * truth to the operator and, through them, to every worker who is clocked in
 * when that date arrives.
 *
 * `when` is a formatted instant, produced by {@link formatScheduleInstant} from
 * the operator's own choice, so the sentence never states a time this file made
 * up and never states one in a zone the reader is not in.
 */
export function stricterModeNoteScheduled(when: string): string {
  return (
    `Nothing changes today. This is saved now and begins to govern at ${when}. ` +
    'A worker who is clocked in when that time arrives keeps the rules their shift ' +
    'started under until their next punch, so a stricter mode reaches each worker at ' +
    'whichever comes later — the time above, or the end of the shift they are already in. ' +
    'Workers see the mode on their policy card in the app — there is no acknowledgement ' +
    'step and nothing announces the change, so tell them yourself if they need warning.'
  );
}

/** The ON warning. See {@link BREADCRUMBS_ON_WARNING} for the unscheduled half. */
export function breadcrumbsOnWarningScheduled(when: string, audience: PolicyAudience): string {
  return (
    `No location points are collected today. This is saved now and begins to govern at ${when}, ` +
    `for ${audienceEveryoneShort(audience)}. A worker already clocked in at that moment is not ` +
    'collected from until their next clock-in, so collection starts for each worker at ' +
    'whichever comes later. Workers see it on their policy card in the app — “a location ' +
    'point every N minutes. Never when you’re clocked out.” — the next time they open that ' +
    'screen, and nothing on their phone announces the change.'
  );
}

/**
 * WHICH BRANCH A NOTE IS IN — the form's own answer, not a re-derivation.
 *
 * `unscheduled` and `stated`-at-now are DIFFERENT ACTS with the same effect
 * today (the server keeps them apart on the wire and in the version series), so
 * this type keeps them apart on the screen too. An operator who opens the picker
 * and chooses this minute has scheduled something; one who left the picker
 * closed has not, and the note they read says so.
 */
export type ScheduleStatement = { kind: 'immediate' } | { kind: 'at'; when: string };

export function stricterModeNote(statement: ScheduleStatement, audience: PolicyAudience): string {
  return statement.kind === 'immediate'
    ? stricterModeNoteFor(audience)
    : stricterModeNoteScheduled(statement.when);
}

export function breadcrumbsOnWarning(
  statement: ScheduleStatement,
  audience: PolicyAudience,
): string {
  return statement.kind === 'immediate'
    ? breadcrumbsOnWarningFor(audience)
    : breadcrumbsOnWarningScheduled(statement.when, audience);
}

/** Modes stricter than the saved one trigger the acknowledgement note. */
export const STRICTER_MODES: ReadonlySet<string> = new Set(['WARN_OUTSIDE', 'REQUIRE_GEOFENCE']);

/**
 * The ON warning. It used to end "— before it applies to them", which asserted
 * prior notice the product cannot give (see STRICTER_MODE_NOTE for why). It
 * also quoted a card that `apps/worker-mobile` mounted nowhere: `PolicyCard`
 * was exported from `@cwos/ui-mobile` and imported by no screen, so "workers
 * see it on their policy card" was false at the moment an operator relied on
 * it. The card is now mounted on the worker's More screen and this sentence
 * states when it is read, rather than implying it is read first.
 */
export function breadcrumbsOnWarningFor(audience: PolicyAudience): string {
  return (
    `Turning this on starts collecting location points during every shift, for ` +
    `${audienceEveryoneShort(audience)}, as soon as you save. Workers see it on their policy ` +
    'card in the app — “a location point every N minutes. Never when you’re clocked out.” — ' +
    'the next time they open that screen. Anyone already clocked in is not collected from ' +
    'until their next clock-in, and nothing on their phone announces the change.'
  );
}

export const BREADCRUMBS_ON_WARNING = breadcrumbsOnWarningFor(ORGANIZATION_AUDIENCE);

export const BREADCRUMBS_OFF_WARNING =
  'Turning this off stops shift location evidence: coverage and trails will have nothing to show for new shifts. Points already collected keep their retention window.';

export const MODE_OFF_TRACKING_NOTE =
  'Location is off in General, so nothing is collected regardless of these settings. They take effect when a collection mode is turned on.';

/** The real enforcement mechanism behind the retention window (advanced detail). */
export const PURGE_JOB_NAME = 'system.breadcrumb-purge';

/**
 * What actually enforces the retention window, in the cadence it actually runs.
 *
 * This said "a real nightly sweep" while `PGBOSS_BREADCRUMB_PURGE_CRON` has
 * defaulted to `'17 * * * *'` — hourly — since the job was written
 * (apps/api/src/config/env.ts). The error was conservative (the sweep runs more
 * often than the page claimed, not less) but it was still an administrator being
 * told a cadence nothing ran at, and a component test pinned the wrong word in
 * place. It is a constant now so the page and the test cannot disagree about it,
 * and so there is one place to change when the cron changes.
 */
export const PURGE_SWEEP_SENTENCE =
  'Deletion is enforced by a real hourly sweep, not a promise: points older than this window are removed permanently.';

/**
 * The workforce-vs-EVV separation, in the design doc's plain language: the
 * work_geofence table deliberately has no client reference.
 */
export const EVV_SEPARATION_SENTENCE =
  'Workforce location and EVV evidence are separate domains. Geofences cover work sites only — never client homes; matching a location to a client’s home belongs to EVV, with its own consent model.';

export const UNSAVED_PROMPT = 'You have unsaved changes on this page. Leave without saving them?';

/**
 * The fixed geofence guardrail, preserved VERBATIM from the retired
 * GeofenceEditor (guardrail D-1): it was non-overridable there and it is a
 * constant here, so no call site can soften it.
 */
export const WORK_SITES_ONLY_HELPER = 'Work sites only — never client homes.';

/**
 * What attaching a geofence to a program means — the evaluator's real rule
 * (repository.listApplicableGeofences: a program-scoped fence checks only that
 * program's punches, first; an unattached fence checks every program's).
 */
export const PROGRAM_ATTACHMENT_MEANING =
  'A geofence attached to a program is checked only for that program’s punches. A geofence with no program applies to every program.';

/**
 * Why there is no Delete control on the Geofences page: the API has no
 * `DELETE /location/geofences/:id` — a fence a punch was once verified against
 * must stay resolvable, so the lifecycle is active/inactive. An inactive fence
 * never participates in punch checks (listApplicableGeofences filters on
 * status = 'active'). When a delete route ships, the control joins it
 * (settingsNav.ts rule 2: absent, not decorative).
 */
export const NO_DELETE_MEANING =
  'A geofence is never deleted — punches already verified against it keep their evidence. Deactivate it instead: an inactive geofence is not checked against any punch.';

/**
 * Where Work Site management lives, stated on the hub's Work sites page: the
 * `location` entity already has a full CRUD home at Settings → Sites &
 * locations, so this page is the location LENS over it, never a second editor.
 */
export const SITES_MANAGED_ELSEWHERE =
  'Sites are managed under Settings → Sites & locations — addresses, map positions, programs, and each site’s own geofence. This page shows them through the location lens: which work sites a punch can be verified against.';

/**
 * One selection card per policy mode (directive §3): what is collected, when,
 * what the worker experiences, what the admin sees, whether anything can
 * block, the privacy implication, and the exception behaviour. The `value` is
 * the wire enum; it is shown ONLY inside the advanced-details disclosure.
 */
export interface ModeCard {
  value: location.PolicyMode;
  title: string;
  summary: string;
  isDefault?: boolean;
  facets: {
    collected: string;
    when: string;
    worker: string;
    admin: string;
    blocking: string;
    privacy: string;
    exceptions: string;
  };
}

export const MODE_CARDS: readonly ModeCard[] = [
  {
    value: 'OFF',
    title: 'Off',
    summary: 'No location is collected on shifts at all.',
    facets: {
      collected: 'Nothing. No coordinates at clock-in or clock-out, and no points during shifts.',
      when: 'Never.',
      worker:
        'The app never asks the device for a location on a shift; the worker’s policy card shows “Location off”.',
      admin: 'Punches carry an honest “location off” state — there is no map evidence to see.',
      blocking: 'Nothing can block a punch. Clocking in and out works everywhere.',
      privacy: 'The most private setting: no shift location ever leaves the phone.',
      exceptions: 'None — there is nothing to check, so no exception can be raised.',
    },
  },
  {
    value: 'RECORD_ONLY',
    title: 'Record only',
    summary: 'Location is noted at each punch but never judged against a work site.',
    isDefault: true,
    facets: {
      collected:
        'One location at each clock-in and clock-out. Shift points are collected only if turned on under Location tracking.',
      when: `${CLOCK_COLLECTION_WHEN} (plus during shifts, if shift points are on).`,
      worker:
        'Clocking in and out works exactly as before — never a warning, never a question, never a stop.',
      admin:
        'Where each punch happened, shown as “Location noted” — it is never marked inside or outside a work site.',
      blocking: 'Nothing can block a punch.',
      privacy:
        'Collection without judgement: the location is recorded, and no verification claim is ever attached to it.',
      exceptions: 'None are raised — nothing is evaluated, so nothing can be flagged.',
    },
  },
  {
    value: 'WARN_OUTSIDE',
    title: 'Warn outside a work site',
    summary: 'Punches are checked against work sites; being outside warns, never blocks.',
    facets: {
      collected: 'One location at each clock-in and clock-out, checked against your work sites.',
      when: CLOCK_COLLECTION_WHEN,
      worker: 'A heads-up when clocking in away from a work site — the punch always goes through.',
      admin:
        'Each punch shows an honest verification state: at a work site, outside one, or unverifiable. A poor GPS fix can never count as “outside”.',
      blocking: 'Nothing can block a punch.',
      privacy:
        'Punch locations are evaluated, not just recorded; the check is stated on the worker’s policy card in the app, which they read when they open it — there is no notice sent before the change lands.',
      exceptions:
        'A punch verified outside a work site is recorded as an exception for review beside the time — the time itself is never discarded.',
    },
  },
  {
    value: 'REQUIRE_GEOFENCE',
    title: 'Require a work site',
    summary:
      'Clocking in away from a work site asks the worker to attest — or refuses, if attestation is off.',
    facets: {
      collected: 'One location at each clock-in and clock-out, checked against your work sites.',
      when: CLOCK_COLLECTION_WHEN,
      worker:
        'Away from a work site — or with no usable GPS — the worker is asked to attest to where they are in their own words. An attested punch is recorded as attested, never shown as verified.',
      admin:
        'Verified, attested, and unverifiable punches are all labelled honestly; attested time arrives with the worker’s own words attached.',
      blocking:
        'Only if the attested-override allowance below is turned off: then a punch that cannot be confirmed at a work site is refused.',
      privacy:
        'The strictest setting — location decides what the worker is asked at the door. It applies from the moment you save, or from the time you schedule it for; the worker’s policy card states it, but nothing warns them in advance.',
      exceptions:
        'An outside or unverifiable punch records an exception with the worker’s attestation beside the time; with the allowance off, it is refused instead.',
    },
  },
];

/** The product-default card (Record only) — the resolver's own fallback. */
export const DEFAULT_MODE_CARD: ModeCard = MODE_CARDS.find((card) => card.isDefault) as ModeCard;

export function modeCardOf(mode: string): ModeCard {
  return MODE_CARDS.find((card) => card.value === mode) ?? DEFAULT_MODE_CARD;
}

/** Plain-language name for a mode, for lists and override rows. */
export function modeLabel(mode: string): string {
  return modeCardOf(mode).title;
}

export const ATTESTED_OVERRIDE_LABEL =
  'Allow a worker to attest in their own words when a work site can’t confirm them';

export const ATTESTED_OVERRIDE_OFF_WARNING =
  'With this off, a punch that can’t be confirmed at a work site is refused outright — a worker with no GPS signal cannot clock in until they reach a work site.';

export const CLOCK_REQUIRED_HONESTY =
  'If the device can’t provide a location, the punch still lands with an honest “no location” state — a worker’s time is never discarded.';

// --- Clock Rules: the exception configuration (design doc §6; L117) ---------

/**
 * The one sentence the whole exception vertical hangs on — the backend's
 * binding integrity rule, stated where the toggles that shape exceptions live.
 */
export const EXCEPTION_INTEGRITY_SENTENCE =
  'A location exception is a record beside the punch, never a gate in front of it: the worked time always lands, and the exception is what a person reviews.';

export const EXCEPTION_REASON_LABEL =
  'Ask the worker for a reason, in their own words, when a punch records a location exception';

export const EXCEPTION_REASON_ON_NOTE =
  'The worker app asks for the worker’s own words before it submits an attestation. A punch is never refused over a missing reason — an exception without one is simply marked as waiting for words.';

export const EXCEPTION_APPROVAL_LABEL =
  'Require a supervisor to decide each new location exception';

export const EXCEPTION_APPROVAL_ON_NOTE =
  'New exceptions open as “waiting for a decision” and appear in the review queue until a supervisor approves or returns them. The worked time is payable either way — a decision is about the record, not the pay.';

export const EXCEPTION_APPROVAL_OFF_NOTE =
  'New exceptions are recorded for review but nobody owes a decision on them.';

/** Plain-language names for the objective exception kinds. */
export const EXCEPTION_KIND_LABELS: Record<string, string> = {
  outside_geofence: 'Outside a work site',
  no_gps_fix: 'No GPS position',
  permission_denied: 'Location access denied on the device',
  accuracy_too_low: 'Position too imprecise to place',
  site_inactive: 'Only inactive work sites in scope',
  missing_site: 'No work site to check against',
};

/** What each kind means, in a reviewer's words — states, never findings. */
export const EXCEPTION_KIND_MEANINGS: Record<string, string> = {
  outside_geofence: 'The punch was recorded outside every applicable work-site geofence.',
  no_gps_fix: 'The device could not get a position when the punch was made.',
  permission_denied: 'The worker’s device denied location access at the punch.',
  accuracy_too_low: 'A position arrived, but too imprecise to prove inside or outside.',
  site_inactive: 'The work sites that would apply here are all marked inactive.',
  missing_site: 'No work-site geofence was configured for this punch’s scope.',
};

export const EXCEPTION_STATUS_LABELS: Record<string, string> = {
  recorded: 'Recorded',
  pending_approval: 'Waiting for a decision',
  approved: 'Approved',
  returned: 'Returned',
};

// --- the derivable detections (§9's issues surface, folded into Exceptions) --

/**
 * The one sentence that frames both detections: observations over what the
 * clock already recorded — objective device/policy states, never findings
 * about a person, and nothing here changes any worked time.
 */
export const DETECTIONS_FRAMING_SENTENCE =
  'These are read from the punches already recorded — objective device and policy states, not findings about anyone. Nothing here changes worked time.';

export const MISSING_EVIDENCE_HEADING = 'Punches missing expected location evidence';

export const MISSING_EVIDENCE_EXPLAINER =
  'Your clock rules ask for a location on these punches, and the device recorded none. Each punch still landed with its honest state.';

export const MISSING_EVIDENCE_STATE_LABELS: Record<string, string> = {
  unavailable_no_fix: 'No GPS position',
  unavailable_permission_denied: 'Location access denied on the device',
};

export const PERMISSION_STREAK_HEADING = 'Location permission off across working days';

export const PERMISSION_STREAK_EXPLAINER =
  'Every recent working day for these workers recorded location permission as disabled, with no position kept. Often a phone setting or a new device — a conversation, not a conclusion.';

// --- WHEN A SAVE BEGINS TO GOVERN (lane SCHEDUI) ----------------------------
//
// The founder's ruling of 2026-09-08, which the server has implemented since
// migration 1974000000000 and which no operator could reach until this control
// existed:
//
//   "Future effective values must not apply immediately."
//
//   "An increase or mixed change governs at the LATER OF its scheduled
//    effective timestamp and the worker's next eligible disclosed shift
//    boundary. A reduction may apply immediately UNLESS EXPLICITLY SCHEDULED
//    FOR LATER."
//
// TWO FLOORS, AND THE SCREEN STATES BOTH. The date the operator picks is one of
// them. The other belongs to each worker and is a different instant for every
// one of them, so it is named rather than dated — the exact discipline
// `policy-schedule.ts` applies when it returns `governsFrom: null` for an
// undetermined maximum. A screen that showed only the date would be telling
// half the truth to the person deciding whether to track other people.

export const SCHEDULE_LEGEND = 'When this begins';

/**
 * TWO ACTS, NOT ONE CONTROL WITH A DEFAULT.
 *
 * The wire keeps "no schedule stated" and "scheduled for this instant" apart
 * (`scheduledEffectiveAt: null` clears a schedule; an instant states one), the
 * version series records which of the two an operator did, and this radio is
 * the only place a human chooses between them. Collapsing them here would make
 * the distinction unanswerable at exactly the point it is created.
 */
export const SCHEDULE_IMMEDIATE_LABEL = 'As soon as I save';
export const SCHEDULE_AT_LABEL = 'At a time I choose';
export const SCHEDULE_AT_FIELD_LABEL = 'Date and time this begins';

/**
 * WHOSE CLOCK THE PICKER IS ON, read from the browser rather than assumed.
 *
 * A `datetime-local` input carries no zone at all, so the instant it means
 * depends entirely on where the reader is sitting — and this product's own lane
 * contract records that a wrong zone is worth 416 hours a year. The zone is
 * therefore RESOLVED and printed, never named in a constant: a hard-coded
 * "Central time" would be wrong for every operator who is not in it, and
 * silently wrong, which is worse.
 */
export function scheduleZoneSentence(timeZone: string): string {
  return `Times are read in ${timeZone}, the time zone this browser is set to. Phibian stores the exact instant that names, so it means the same moment for every worker wherever they are.`;
}

/** The browser's own IANA zone, or a stated absence — never a guessed one. */
export function browserTimeZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && zone.length > 0 ? zone : 'this browser’s time zone';
  } catch {
    return 'this browser’s time zone';
  }
}

/**
 * One instant, rendered for a human, WITH ITS ZONE.
 *
 * `timeZoneName` is not decoration: the whole subject of this control is when
 * something happens, and a time printed without its zone is the ambiguity the
 * date-only column died of. Every sentence on this screen that names an instant
 * goes through this function, and so does every test assertion about one — a
 * typed string would pass in one CI timezone and fail in another, which is the
 * same defect wearing a green suite.
 */
export function formatScheduleInstant(iso: string): string {
  const at = new Date(iso);
  if (!Number.isFinite(at.getTime())) return 'an instant Phibian could not read';
  // EXPLICIT COMPONENTS, NOT `dateStyle`/`timeStyle`. Those two shortcuts may
  // not be combined with any other component option, and `Intl` throws
  // `TypeError: Invalid option : option` when they are — which the first draft
  // of this function did, taking the whole settings page down behind an error
  // boundary the moment a schedule existed to print. Measured: eight rows of
  // the schedule suite went red, and every one of them was a screen that had
  // stopped rendering rather than a sentence that was wrong.
  return at.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export const SCHEDULE_AT_REQUIRED =
  'Choose the date and time this change should begin, or switch back to “As soon as I save”.';

/**
 * A PAST INSTANT IS NOT REFUSED, AND IT IS NOT HONOURED EITHER.
 *
 * `location_policy_version.governs_from` is GENERATED as
 * `greatest(effective_at, coalesce(scheduled_effective_at, effective_at))`, so a
 * stated instant in the past is clamped to the moment the values were recorded
 * — a publisher cannot backdate a disclosure into a shift that has already run.
 * The form allows it (it is legitimate evidence of intent, and the operator may
 * be recording a decision made earlier) and says what will actually happen,
 * rather than refusing input the server accepts.
 */
export const SCHEDULE_PAST_NOTE =
  'That time has already passed. Phibian will not backdate a tracking change into shifts that have already run, so this will begin from the moment you save — the stated time is kept as a record of what you asked for.';

/**
 * WHAT IS ALREADY COMING — the heading over a change this scope has published
 * whose instant has not arrived.
 *
 * Absent this block the settings form is blind to its own operator's schedule:
 * `GET /location/policy/effective` answers with the version IN FORCE, so the
 * form would show pre-change values, no schedule, and no trace that anything is
 * pending.
 */
export const PENDING_HEADING = 'A change is already scheduled';

/**
 * AND THE OTHER HEADING — a change scheduled somewhere ABOVE this scope.
 *
 * Deliberately not `PENDING_HEADING`. On a program editor the two blocks appear
 * one above the other, and if they carried the same words the only thing
 * separating "decide about this" from "this is not yours to decide about" would
 * be the presence of radios below it.
 */
export const ORGANIZATION_PENDING_HEADING = 'Your organization has a change scheduled';

/**
 * AND WHAT SAVING NOW DOES TO IT — which is now a QUESTION and no longer a
 * warning, because the founder's ruling of 2026-09-08 made it one.
 *
 *   "If a pending scheduled revision exists, the operator must be shown that
 *    state and explicitly choose the relationship of the new change to it."
 *
 * WHAT THIS SENTENCE USED TO SAY, AND WHY IT COULD NOT SURVIVE. Lane SCHEDUI
 * wrote, correctly for the mechanism as it then stood: "Saving now does not
 * cancel it ... To replace it, schedule this save for that same time or later —
 * the most recently saved change wins at the same moment." Every clause of that
 * is now wrong in a different way, and each one is why the ruling exists:
 *
 *   * "Saving now does not cancel it" was ADVICE ABOUT AN OMISSION. It is no
 *     longer possible to save without answering, so there is nothing to warn
 *     about.
 *   * "schedule this save for that same time" is now REFUSED. Two live
 *     revisions claiming one instant have no order in the data — only a
 *     tie-break, and the query that told the operator what was coming and the
 *     query that decided what arrived broke it in opposite directions. A
 *     partial unique index refuses it in the database and the door refuses it
 *     with a sentence.
 *   * "the most recently saved change wins" was the tie-break itself, offered
 *     to an operator as a feature. Superseding is now an ACT with a reason and
 *     an actor, not a race won by saving last.
 */
export const PENDING_CHOICE_LEGEND = 'What should happen to it?';

export function pendingChoicePrompt(when: string): string {
  return (
    `A change is already scheduled to take effect at ${when}. Phibian will not decide for you ` +
    'what this save does to it — choose one, and the choice is recorded with your name and ' +
    'the reason you give.'
  );
}

/**
 * THE FOUR, IN THE OPERATOR'S WORDS. Keyed by the server's own closed
 * vocabulary (`location.PENDING_RELATIONSHIPS`), never retyped beside it: a
 * fifth relationship is a new ruling, and a screen carrying a fifth label the
 * server does not accept would offer an act that cannot happen.
 */
export const PENDING_RELATIONSHIP_LABELS: Record<string, string> = {
  keep: 'Keep it. This save applies until that time, then the scheduled change takes over.',
  supersede: 'Replace it. This save takes its place and the scheduled change never happens.',
  cancel: 'Cancel it. The scheduled change is withdrawn and nothing takes its place.',
  sequence: 'Schedule this one after it, so both happen in order.',
};

export function pendingRelationshipLabel(relationship: string): string {
  return (
    PENDING_RELATIONSHIP_LABELS[relationship] ??
    `an option this screen does not recognise (${relationship})`
  );
}

export const PENDING_REASON_LABEL = 'Why is the scheduled change being withdrawn?';
export const PENDING_REASON_HINT =
  'Kept with the withdrawn change, alongside your name and the time it was originally set for. This is what an audit reads later.';
export const PENDING_REASON_REQUIRED =
  'A reason is required to withdraw a scheduled change. Nothing is saved without one.';
export const PENDING_CHOICE_REQUIRED =
  'Choose what this save does to the change that is already scheduled.';

/** The heading over the timeline a save produced. */
export const TIMELINE_HEADING = 'What happens now';
export const TIMELINE_NOTHING_UPCOMING = 'Nothing else is scheduled after this.';

export function timelineWithdrewNote(kind: string, when: string): string {
  return kind === 'superseded'
    ? `The change that was set for ${when} was replaced by this save and will not happen.`
    : `The change that was set for ${when} was cancelled and will not happen.`;
}

export function timelineUpcomingNote(when: string): string {
  return `A change is still scheduled for ${when} and will take over then.`;
}

/**
 * THE FLOORS A SAVE IS STILL WAITING ON, named one at a time.
 *
 * The server sends `waitingOn` as a list of floor keys and an `explanation`
 * sentence. This turns each key into the operator's own words. It is a closed
 * map over the wire vocabulary: an unrecognised floor renders as an explicit
 * unknown rather than being dropped, because a floor silently omitted reads as
 * a floor that has been crossed.
 */
export const FLOOR_LABELS: Record<string, string> = {
  schedule: 'the time you scheduled it for',
  shift_boundary: 'the end of the shift each worker is already in',
};

export function floorLabel(floor: string): string {
  return FLOOR_LABELS[floor] ?? `a condition this screen does not recognise (${floor})`;
}

/** The heading over the report a save produces. */
export const SAVED_SCHEDULE_HEADING = 'What you just saved';

export function savedInForceFor(audience: PolicyAudience): string {
  return `These rules are in force now for ${audienceEveryoneShort(audience)} who is not already clocked in.`;
}

export const SAVED_IN_FORCE = savedInForceFor(ORGANIZATION_AUDIENCE);

export function savedGovernsFrom(when: string): string {
  return `These rules are saved and begin to govern at ${when}.`;
}

// ---------------------------------------------------------------------------
// PROGRAM SCOPE — the founder's REVREL follow-up ruling 1 of 2026-09-08
// ---------------------------------------------------------------------------
//
//   "Do not leave program overrides permanently uneditable while a future
//    revision exists... The current disabled state is acceptable only as a
//    TEMPORARY FAIL-CLOSED CONDITION until that customer-facing control is
//    implemented."
//
//   "Program overrides are first-class policy configuration and must not become
//    a weaker scheduling surface than organization-level policy."
//
// WHAT USED TO BE HERE. `PROGRAM_PENDING_LOCKED` — the sentence a program row
// printed instead of a control, because the row was a mode dropdown and an
// Update button and neither can state a relationship to a waiting revision. It
// is gone, not softened: the program scope now has the SAME saving form the
// organization has, so there is nothing left to apologise for. Its removal is
// the deliverable, and a fixture asserting its absence would only prove a
// string is missing — what proves the gap is closed is the program editor
// exercising all four relationships, which the suites do.

/** The title of the program-scoped policy editor, named for the program. */
export function programPolicyHeading(name: string): string {
  return `Location policy for ${name}`;
}

/**
 * WHO A PROGRAM POLICY REACHES, said once at the top of the editor.
 *
 * The scheduled-change sentences below this never name a scope ("a worker who
 * is clocked in when that time arrives..."), which is true at either scope and
 * complete at neither. This is the sentence that makes them complete here, and
 * it also states the precedence the resolver actually implements — a program
 * row wins over the organization row, including over a change the organization
 * has scheduled but not yet had arrive.
 */
export function programScopeNote(name: string): string {
  return (
    `These rules apply only to workers on a shift in ${name}. Everyone else keeps the ` +
    'organization policy. While this program has its own policy it also keeps it when the ' +
    'organization policy changes, including a change the organization has already scheduled.'
  );
}

/**
 * A PROGRAM WITH NO POLICY OF ITS OWN, on the editor that is about to give it
 * one. `source` is the server's own answer, so this never guesses.
 */
export function programInheritsNote(name: string, source: string): string {
  return source === 'product_default'
    ? `${name} has no policy of its own and your organization has none stored either, so the values below are Phibian's product default. Saving stores them as this program's own policy.`
    : `${name} has no policy of its own and follows the organization policy. The values below are the ones governing it today; saving stores them as this program's own policy, and it stops following the organization from that moment.`;
}

/**
 * THE ORGANIZATION'S SCHEDULED CHANGE, SHOWN AS THE ORGANIZATION'S.
 *
 * `GET /location/policy/effective` answers two different questions and the
 * difference is the whole of this sentence. `pendingRevisions` is what is
 * waiting on the scope THIS FORM WRITES; `pending` is the next change that
 * alters what governs HERE, and for a program with no override of its own that
 * is the ORGANIZATION's revision, published by somebody else on another screen.
 *
 * A program editor that rendered the second as the first would ask the operator
 * to keep, supersede or cancel a revision belonging to a scope this form does
 * not write — and the server would answer NOT_PENDING, because that version is
 * not in this scope's live set. So it is rendered HERE, with no choices, saying
 * whose it is and what happens to it.
 */
export function organizationPendingNote(when: string): string {
  return (
    `Your organization has a policy change scheduled for ${when}. It belongs to the ` +
    'organization, not to this program, so there is nothing to decide about it here — and ' +
    'once this program has its own policy, that change will not reach it. Edit it on the ' +
    'organization pages.'
  );
}

/** The link out of the override list into one program's full policy editor. */
export function programEditLinkLabel(name: string): string {
  return `Edit the location policy for ${name}`;
}

export const PROGRAM_OVERRIDE_EDIT_LABEL = 'Edit policy';

/** The list's own line for a program whose scope has a revision waiting. */
export function programHasScheduledChange(when: string): string {
  return `A change is scheduled for this program at ${when}.`;
}

export const PROGRAM_ADD_OVERRIDE_LABEL = 'Set up an override';

export const PROGRAM_ADD_OVERRIDE_HINT =
  'Opens that program’s own policy editor, seeded with the rules governing it today. Nothing is stored until you save there.';

/** Back to the hub page the override list lives on. */
export const PROGRAM_BACK_LABEL = 'Back to per-program policies';

export const PROGRAM_SAVE_LABEL = 'Save this program’s policy';
